import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import type { Actor, Space } from "@melo/shared";
import { initials } from "../lib/select";
import { dismiss, useToasts } from "../lib/toast";
import markSvg from "../lib/mark.svg?raw";
import { Icon } from "./icons";

export function Mark({ className = "mark" }: { className?: string }) {
  return <span className={className} style={{ display: "inline-grid" }} aria-hidden="true" dangerouslySetInnerHTML={{ __html: markSvg.replace("<svg", '<svg width="100%" height="100%"') }} />;
}

export function Avatar({ actor, size }: { actor: Actor | undefined; size?: "sm" | "lg" }) {
  if (!actor) return null;
  const label = `${actor.name}${actor.type === "agent" ? " (agent)" : ""}`;
  return (
    <span className={`av${actor.type === "agent" ? " bot" : ""}${size ? ` ${size}` : ""}`} style={{ background: actor.color }} title={label} aria-label={label} role="img">
      {initials(actor)}
    </span>
  );
}

export function Avatars({ actors, max = 3 }: { actors: (Actor | undefined)[]; max?: number }) {
  const list = actors.filter((a): a is Actor => Boolean(a));
  if (!list.length) return null;
  return (
    <span className="avs">
      {list.slice(0, max).map((a) => (
        <Avatar key={a.id} actor={a} size="sm" />
      ))}
      {list.length > max && <span className="av sm" style={{ background: "var(--line)", color: "var(--text-2)" }}>+{list.length - max}</span>}
    </span>
  );
}

export function SpaceDot({ space }: { space: Pick<Space, "color" | "prefix"> | undefined }) {
  if (!space) return null;
  return (
    <span className="gdot" style={{ background: space.color }} aria-hidden="true">
      {space.prefix.slice(0, 2)}
    </span>
  );
}

/**
 * Overlays stack: the item panel, then dialogs and the lightbox, then the command menu, then menus.
 * A click inside a higher layer never dismisses a lower one, and Escape closes only the top one.
 */
export const LAYER = { panel: 1, dialog: 2, palette: 3, menu: 4 } as const;
const openLayers: { id: number; layer: number; close: () => void }[] = [];
let nextLayerId = 1;

if (typeof document !== "undefined") {
  // Bubble phase, so a field that handles Escape itself (cancel an edit) goes first.
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape" || !openLayers.length || e.defaultPrevented) return;
    const target = e.target as HTMLElement;
    // Editing in place on the item panel: the first Escape leaves the field; the next one closes the panel.
    // Forms in dialogs close on the first Escape, like native dialogs.
    const inPanel = target.closest?.("[data-layer]")?.getAttribute("data-layer") === String(LAYER.panel);
    if (inPanel && target.matches?.("input, textarea, select, [contenteditable]") && !target.hasAttribute("data-escape-closes")) {
      target.blur();
      return;
    }
    const top = openLayers.reduce((a, b) => (b.layer > a.layer || (b.layer === a.layer && b.id > a.id) ? b : a));
    e.preventDefault();
    top.close();
  });
}

/** Closes on an outside click (below this layer) and on Escape when topmost; returns focus to the opener. */
export function useDismiss(ref: RefObject<HTMLElement | null>, onClose: () => void, active = true, layer: number = LAYER.menu) {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!active) return;
    const opener = document.activeElement as HTMLElement | null;
    const entry = { id: nextLayerId++, layer, close: () => close.current() };
    openLayers.push(entry);
    const down = (e: PointerEvent) => {
      const target = e.target as Element;
      if (!ref.current || ref.current.contains(target)) return;
      const hit = target.closest?.("[data-layer]");
      if (hit && Number(hit.getAttribute("data-layer")) > layer) return;
      if (target.closest?.(".toasts")) return;
      close.current();
    };
    document.addEventListener("pointerdown", down, true);
    return () => {
      document.removeEventListener("pointerdown", down, true);
      openLayers.splice(openLayers.indexOf(entry), 1);
      if (opener && document.contains(opener) && !openLayers.length) opener.focus({ preventScroll: true });
    };
  }, [ref, active, layer]);
}

/** A floating panel placed next to its anchor, kept inside the window. */
export function Popover({ anchor, onClose, children, align = "start", width }: { anchor: HTMLElement | null; onClose: () => void; children: ReactNode; align?: "start" | "end"; width?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useDismiss(ref, onClose, true, LAYER.menu);
  useLayoutEffect(() => {
    if (!anchor || !ref.current) return;
    const a = anchor.getBoundingClientRect();
    const p = ref.current.getBoundingClientRect();
    let left = align === "end" ? a.right - p.width : a.left;
    left = Math.max(12, Math.min(left, window.innerWidth - p.width - 12));
    let top = a.bottom + 6;
    if (top + p.height > window.innerHeight - 12) top = Math.max(12, a.top - p.height - 6);
    setPos({ top, left });
  }, [anchor, align]);
  return createPortal(
    <div ref={ref} className="pop" data-layer={LAYER.menu} style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, width }} role="dialog">
      {children}
    </div>,
    document.body,
  );
}

export interface MenuItem {
  id: string;
  label: string;
  icon?: ReactNode;
  checked?: boolean;
  danger?: boolean;
  hint?: string;
  run: () => void;
}

/** A searchable list for pickers. Arrow keys move, Enter picks. */
export function MenuList({ items, onPick, searchable, placeholder = "Search", multi, onCreate, createLabel = "Create" }: { items: MenuItem[]; onPick?: () => void; searchable?: boolean; placeholder?: string; multi?: boolean; onCreate?: (text: string) => void; createLabel?: string }) {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const filtered = q ? items.filter((i) => i.label.toLowerCase().includes(q.toLowerCase())) : items;
  // Typing a name that does not exist offers to create it.
  const creatable = onCreate && q.trim() && !items.some((i) => i.label.toLowerCase() === q.trim().toLowerCase());
  const shown: MenuItem[] = creatable
    ? [...filtered, { id: "__create", label: `${createLabel} "${q.trim()}"`, run: () => (onCreate!(q.trim()), setQ("")) }]
    : filtered;
  useEffect(() => {
    if (searchable) input.current?.focus();
    else (list.current?.querySelector(".mi") as HTMLElement | null)?.focus();
  }, [searchable]);
  const pick = (i: MenuItem) => {
    i.run();
    if (!multi) onPick?.();
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, shown.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter" && shown[active]) {
      e.preventDefault();
      pick(shown[active]!);
    }
  };
  return (
    <div onKeyDown={onKey} ref={list}>
      {searchable && <input ref={input} data-escape-closes className="pop-search" placeholder={placeholder} value={q} onChange={(e) => (setQ(e.target.value), setActive(0))} aria-label={placeholder} />}
      <div role="listbox" aria-multiselectable={multi || undefined}>
        {shown.map((i, n) => (
          <button
            key={i.id}
            type="button"
            className={`mi${i.danger ? " danger" : ""}`}
            role="option"
            aria-selected={Boolean(i.checked)}
            aria-checked={i.checked}
            data-active={n === active}
            onMouseEnter={() => setActive(n)}
            onClick={() => pick(i)}
          >
            {i.icon}
            <span className="grow" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {i.label}
            </span>
            {i.hint && <span className="mono muted">{i.hint}</span>}
            {i.checked && <Icon name="check" className="ico tick" />}
          </button>
        ))}
        {!shown.length && <div className="mi muted">No matches</div>}
      </div>
    </div>
  );
}

export function Dialog({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(ref, onClose, true, LAYER.dialog);
  useEffect(() => {
    const first = ref.current?.querySelector<HTMLElement>("input, textarea, select, button:not(.dialog-close)");
    first?.focus();
  }, []);
  return createPortal(
    <>
      <div className="scrim" />
      <div ref={ref} className={`dialog${wide ? " wide" : ""}`} data-layer={LAYER.dialog} role="dialog" aria-modal="true" aria-label={title}>
        <div className="dialog-h">
          <h2>{title}</h2>
          <button type="button" className="btn ghost icon sm dialog-close" onClick={onClose} aria-label="Close">
            <Icon name="x" />
          </button>
        </div>
        <div className="dialog-b">{children}</div>
      </div>
    </>,
    document.body,
  );
}

export function Toasts() {
  const list = useToasts();
  return (
    <div className="toasts" aria-live="polite">
      {list.map((t) => (
        <div key={t.id} className={`toast${t.tone === "error" ? " error" : ""}`} role={t.tone === "error" ? "alert" : "status"}>
          <span>{t.message}</span>
          {t.action && (
            <button
              type="button"
              onClick={() => {
                t.action!.run();
                dismiss(t.id);
              }}
            >
              {t.action.label}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <b>{title}</b>
      {children}
    </div>
  );
}
